import { Stack } from '@mui/material';
import type { ObjectStorageDestination } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { StepContainer } from '../../wizard/StepContainer';
import { BucketSelectionStep } from '../bucketSelectionStep';
import type { RestoreFileState } from '../restoreFileWizard';
import { StorageAccountSelectionStep } from '../storageAccountSelection';

export const FileConfiguration = (props: StepProps<RestoreFileState>) => {
  const dal = useDAL();
  const { wizardState: stepState, stepperLabels, finish, back } = props;
  const {
    S3,
    GCS,
    StorageAccount,
    prefix,
    resourceId,
    snapshot,
    files,
    restoreAccount,
  } = stepState;

  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const cloudProvider = restoreAccount?.restoreAccountAttributes.cloudProvider;
  const restoringToAws = cloudProvider === Provider.Aws;
  const restoringToAzure = cloudProvider === Provider.Azure;
  const restoringToGcp = cloudProvider === Provider.Gcp;

  const onNextClick = async () => {
    setError(undefined);
    setIsRestoring(true);
    const destination: ObjectStorageDestination =
      {} as unknown as ObjectStorageDestination;

    if (restoringToAws) {
      const bucketName = S3.Bucket ?? '';
      // for saving the bucket name between different restores
      void dal.preferences.updateUserPref(PreferencesKey.RestoreBucket, {
        bucketName,
      });
      destination.s3Bucket = { bucketName, prefix };
    } else if (restoringToGcp) {
      const bucketName = GCS.Bucket ?? '';
      // for saving the bucket name between different restores
      void dal.preferences.updateUserPref(PreferencesKey.RestoreGcsBucket, {
        bucketName,
      });
      destination.gcsBucket = { bucketName, prefix };
    } else if (restoringToAzure) {
      const accountName = StorageAccount.AccountName ?? '';
      const resourceGroup = StorageAccount.ResourceGroup ?? '';
      const container = StorageAccount.Container ?? '';
      void dal.preferences.updateUserPref(
        PreferencesKey.RestoreStorageAccount,
        { accountName, container, resourceGroup }
      );
      destination.storageAccount = {
        name: accountName,
        container,
        resourceGroup,
        prefix,
      };
    }

    void dal.restore
      .files(resourceId!, snapshot.id, {
        files,
        restoreAccountId: restoreAccount!.id,
        destination,
      })
      .then(() =>
        finish(`Started restore job for ${files.map((x) => x.path).join(', ')}`)
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setError(`Failed to restore file: ${errorMessage}`);
        setIsRestoring(false);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={stepperLabels}
      stepperIndex={stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={() => {
        if (isRestoring) {
          return false;
        }

        if (restoringToAws) {
          return Boolean(S3.Bucket);
        } else if (restoringToGcp) {
          return Boolean(GCS.Bucket);
        } else if (restoringToAzure) {
          return Boolean(
            StorageAccount.AccountName && StorageAccount.Container
          );
        }
        return false;
      }}
      navigationComponent={
        error && (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        snapshot && (
          <SnapshotToolbar
            pointInTime={snapshot.pointInTime}
            vaultId={snapshot.vaultId}
          />
        )
      }
    >
      {restoringToAws && (
        <BucketSelectionStep
          {...props}
          preferencesKey={PreferencesKey.RestoreBucket}
        />
      )}
      {restoringToAzure && (
        <StorageAccountSelectionStep
          {...props}
          allowUsingSourceContainer={false}
        />
      )}
      {restoringToGcp && (
        <BucketSelectionStep
          {...props}
          preferencesKey={PreferencesKey.RestoreGcsBucket}
        />
      )}
    </StepContainer>
  );
};
